'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import type { CategoryOverviewRow, CategoryRefSource, CreateCustomCategoryRequest, ReviewQueueItem } from '@/types';
import { ApiError, categoryBudgetApi } from '@/lib/api';
import { usePathname, useRouter, useSearchParams } from '@/lib/router';
import { buildCategoryTree, resolveCategoryDetailTarget } from '@/lib/categories/tree';
import { useCurrentMonthScope } from '@/hooks/useCurrentMonthScope';
import { useCategories } from '@/hooks/queries/useCategories';
import { useCategoryOverview } from '@/hooks/queries/useCategoryOverview';
import { useSWRConfig } from 'swr';
import { useAuth } from '@clerk/clerk-react';
import { useReviewQueue } from '@/hooks/queries/useReviewQueue';
import { markHomeFeedSeenIds } from '@/lib/homeFeedSeen';
import {
  revalidateCustomCategories,
  revalidateReviewQueues,
  revalidateTransactionCaches,
  useAuthedFetcher,
} from '@/hooks/queries/swrConfig';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { CategoryDetailDrawer, type CategoryDetailView } from '@/components/transactions/category-detail/CategoryDetailDrawer';
import { TransactionDetailDrawer } from '@/components/transactions/screen/TransactionDetailDrawer';
import { adaptTransactionForScreen } from '@/components/transactions/screen/TransactionsScreen';
import { useTransactionRename } from '@/hooks/useTransactionRename';
import type { ScreenTransaction } from '@/components/transactions/screen/types';
import { buildCategoryIndexFromTaxonomy } from '@/components/transactions/screen/categoryIndex';
import { applyLimitToOverview, reviewItemKey } from './helpers';
import { CategoryRows } from './CategoryRows';
import { GoalLinkPicker } from './GoalLinkPicker';
import { CategoryPickerDrawer } from '@/components/categories/CategoryPickerDrawer';
import { buildRuleAnchors, type RuleSaveOptions } from '@/components/categories/ruleAnchor';
import { retroApplyFeedback } from '@/components/categories/confirmCategoryFeedback';
import { NewCategorySheet, type EditableCategory } from './NewCategorySheet';
import { ReviewQueueCard } from './ReviewQueueCard';
import { useRuleRefs } from './useRuleRefs';
import { track } from '@/lib/telemetry';
import { notifyError } from '@/lib/notify-error';

export function OrcamentoView() {
  const authedFetch = useAuthedFetcher();
  const { userId } = useAuth();

  // The page is date-insensitive (VER-252): it is the taxonomy directory, and limits
  // are monthly by definition, so everything month-scoped reads the CURRENT month
  // implicitly — the global period picker is hidden on this route (AppHeader). The
  // scope is stable across renders and rolls over at the month boundary.
  const monthScope = useCurrentMonthScope();

  const overviewScope = useMemo(
    () => ({ year: monthScope.year, month: monthScope.month }),
    [monthScope],
  );
  const {
    data: overview,
    error: overviewError,
    isLoading: overviewLoading,
    mutate: mutateOverview,
  } = useCategoryOverview(overviewScope);

  // The queue is period-independent: pending review is a to-do list, and whatever the
  // "A revisar" KPI announces must be actionable right below regardless of the period.
  // Category reviews only — refund reviews live on the /transactions page.
  const { data: queue, mutate: mutateQueue } = useReviewQueue('category');

  const [savingLimitRef, setSavingLimitRef] = useState<string | null>(null);
  // Income → meta picker: the row being linked (null = closed).
  const [goalPickerRow, setGoalPickerRow] = useState<CategoryOverviewRow | null>(null);
  const [savingGoalLink, setSavingGoalLink] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [categorizing, setCategorizing] = useState<ReviewQueueItem | null>(null);
  const [categorizingBatch, setCategorizingBatch] = useState<string[] | null>(null);
  // The card starts minimized (accordion); the KPI click expands it.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [categorizeSaving, setCategorizeSaving] = useState(false);
  const [newCategoryOpen, setNewCategoryOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  // Kebab actions.
  const [editingCategory, setEditingCategory] = useState<EditableCategory | null>(null);
  // Transaction tapped inside the category hub's Lançamentos list. The full
  // transaction (not an id) — the hub fetches its own category page and this page
  // has no transaction list to resolve an id against.
  const [detailTx, setDetailTx] = useState<ScreenTransaction | null>(null);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: pickerCategories } = useCategories(true);
  const ruleRefs = useRuleRefs();
  // Resolves a flat Pluggy category id to its root (for openCategoryDetail's rootId/childId
  // shape) — same tree buildCategoryIndexFromTaxonomy already reconstructs below.
  const categoryTree = useMemo(() => buildCategoryTree(pickerCategories), [pickerCategories]);

  // The category hub is URL-driven (`?catDetail=<ref>`), so the browser back
  // button closes the drawer instead of leaving the page, and a category's
  // detail is deep-linkable. `catDetailSub` pre-selects a subcategory slice;
  // `catDetailView` picks the view (resumo | limite | regras; default is the
  // transaction list — the entries say "Ver lançamentos").
  const detailCategoryRef = searchParams.get('catDetail');
  const detailSubcategoryId = searchParams.get('catDetailSub');
  const detailViewParam = searchParams.get('catDetailView');
  const detailView: CategoryDetailView =
    detailViewParam === 'resumo' ? 'resumo'
    : detailViewParam === 'limite' ? 'limit'
    : detailViewParam === 'regras' ? 'rules'
    : 'transactions';

  const openCategoryDetail = useCallback(
    (rootId: string, childId: string | null, view?: 'resumo' | 'limite' | 'regras') => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('catDetail', rootId);
      if (childId) params.set('catDetailSub', childId);
      else params.delete('catDetailSub');
      if (view) params.set('catDetailView', view);
      else params.delete('catDetailView');
      router.push(`${pathname}?${params.toString()}`);
    },
    [router, pathname, searchParams],
  );

  const closeTransactionDetails = useCallback(() => setDetailTx(null), []);
  // Rename from the category-hub drill-down (VER-377). detailTx is a captured object
  // — the hub fetches its own category page, this page has no list to re-resolve it
  // against — so the single-rename result patches it in place.
  const rename = useTransactionRename({
    surface: 'orcamento',
    onUpdated: (updated) => setDetailTx(
      (current) => (current?.id === updated.id ? adaptTransactionForScreen(updated) : current),
    ),
  });

  // The hub is URL-driven (`?catDetail=`), so the browser Back button can close it
  // underneath the stacked transaction detail — the orphan must close with its host.
  useEffect(() => {
    if (detailCategoryRef == null) setDetailTx(null);
  }, [detailCategoryRef]);

  const closeCategoryDetail = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('catDetail');
    params.delete('catDetailSub');
    params.delete('catDetailView');
    const qs = params.toString();
    // Replace (not push) so closing doesn't grow the history stack.
    router.replace(qs ? `${pathname}?${qs}` : pathname);
  }, [router, pathname, searchParams]);

  const { mutate: mutateGlobal } = useSWRConfig();
  const refresh = useCallback(() => {
    void mutateOverview();
    void mutateQueue();
    // The queue is keyed per kind — home's total and the transactions page's
    // refund slice must refresh along with this page's category slice.
    revalidateReviewQueues(mutateGlobal);
    // Rules/confirms recategorize transactions — every transaction cache (the
    // /transactions infinite list, drill-down lines, detail drawer) must refresh too.
    revalidateTransactionCaches(mutateGlobal);
  }, [mutateOverview, mutateQueue, mutateGlobal]);

  const showError = useCallback((err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.isLimitExceeded()) return; // global upgrade dialog already shown
    notifyError('orcamento', err, fallback);
  }, []);

  const scrollToReview = useCallback(() => {
    setReviewOpen(true);
    document.getElementById('revisar')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSaveLimit = useCallback(
    async (categoryRef: string, limitAmount: number | null) => {
      if (!overview) return;
      // A first limit on a child line needs the root to fabricate its childLimits entry.
      const rootRef = categoryTree.byId.get(categoryRef)?.rootId ?? null;
      setSavingLimitRef(categoryRef);
      try {
        await mutateOverview(
          async (current) => {
            await authedFetch((token) =>
              categoryBudgetApi.upsertLimit(token, categoryRef, { limitAmount }),
            );
            return applyLimitToOverview(current ?? overview, categoryRef, limitAmount, rootRef);
          },
          {
            optimisticData: (current) =>
              applyLimitToOverview(current ?? overview, categoryRef, limitAmount, rootRef),
            rollbackOnError: true,
            populateCache: true,
            // Background refetch corrects summary/burn from the server.
            revalidate: true,
          },
        );
        track('action.budget.set_limit', { surface: 'orcamento', cleared: limitAmount == null });
        toast.success(limitAmount == null ? 'Limite removido.' : 'Limite salvo.');
      } catch (err) {
        showError(err, 'Não foi possível salvar o limite.');
      } finally {
        setSavingLimitRef(null);
      }
    },
    [authedFetch, mutateOverview, overview, categoryTree, showError],
  );

  const handleSaveGoalLink = useCallback(
    async (categoryRef: string, goalId: string | null) => {
      setSavingGoalLink(true);
      try {
        await authedFetch((token) =>
          categoryBudgetApi.linkGoal(token, categoryRef, { goalId }),
        );
        if (goalId != null) track('action.budget.link_goal');
        toast.success(goalId == null ? 'Vínculo removido.' : 'Meta vinculada.');
        setGoalPickerRow(null);
        // Display-only link — only the overview cache carries it.
        void mutateOverview();
      } catch (err) {
        showError(err, 'Não foi possível vincular a meta.');
      } finally {
        setSavingGoalLink(false);
      }
    },
    [authedFetch, mutateOverview, showError],
  );

  const handleConfirm = useCallback(
    async (item: ReviewQueueItem, options: RuleSaveOptions) => {
      setConfirmingId(reviewItemKey(item));
      try {
        const result = await authedFetch((token) =>
          categoryBudgetApi.confirmCategory(token, item.id, {
            categoryRef: null,
            createRule: options.createRule,
            applyToMerchant: options.applyToMerchant,
            ruleAnchor: options.ruleAnchor,
          }),
        );
        const feedback = retroApplyFeedback(result);
        if (feedback) toast[feedback.severity](feedback.message);
        track('action.categorize.apply', {
          source: 'review_queue',
          mode: 'single',
          count: 1,
          rule_created: options.createRule,
        });
        // Reviewed = seen: keep the home feed from flagging this row as Novo.
        if (userId) markHomeFeedSeenIds(userId, [item.id]);
        refresh();
      } catch (err) {
        showError(err, 'Não foi possível confirmar o lançamento.');
      } finally {
        setConfirmingId(null);
      }
    },
    [authedFetch, refresh, showError, userId],
  );

  const handleCategorize = useCallback(
    async (
      item: ReviewQueueItem,
      categoryRef: string,
      options: RuleSaveOptions,
    ) => {
      setCategorizeSaving(true);
      try {
        const result = await authedFetch((token) =>
          categoryBudgetApi.confirmCategory(token, item.id, {
            categoryRef,
            createRule: options.createRule,
            applyToMerchant: options.applyToMerchant,
            ruleAnchor: options.ruleAnchor,
          }),
        );
        const feedback = retroApplyFeedback(result);
        if (feedback) toast[feedback.severity](feedback.message);
        track('action.categorize.apply', {
          source: 'review_queue',
          mode: 'single',
          count: 1,
          rule_created: options.createRule,
        });
        if (userId) markHomeFeedSeenIds(userId, [item.id]);
        setCategorizing(null);
        refresh();
      } catch (err) {
        showError(err, 'Não foi possível categorizar o lançamento.');
      } finally {
        setCategorizeSaving(false);
      }
    },
    [authedFetch, refresh, showError, userId],
  );

  const handleUpdateCategory = useCallback(
    async (id: string, data: { name: string; icon: string | null; color: string | null }) => {
      setCreatingCategory(true);
      try {
        await authedFetch((token) => categoryBudgetApi.updateCategory(token, id, data));
        track('action.category.update', { surface: 'orcamento' });
        toast.success('Categoria atualizada.');
        setEditingCategory(null);
        void mutateOverview();
        revalidateCustomCategories(mutateGlobal);
      } catch (err) {
        showError(err, 'Não foi possível atualizar a categoria.');
      } finally {
        setCreatingCategory(false);
      }
    },
    [authedFetch, mutateOverview, mutateGlobal, showError],
  );

  const handleToggleArchived = useCallback(
    async (row: CategoryOverviewRow) => {
      try {
        // PUT replaces name, icon and color, so the row goes back as it is.
        await authedFetch((token) =>
          categoryBudgetApi.updateCategory(token, row.categoryRef, {
            name: row.name, icon: row.icon, color: row.color, archived: !row.isArchived,
          }),
        );
        track(row.isArchived ? 'action.category.unarchive' : 'action.category.archive', {
          surface: 'orcamento',
        });
        toast.success(row.isArchived
          ? `“${row.name}” desarquivada.`
          : `“${row.name}” arquivada — o histórico fica preservado.`);
        void mutateOverview();
        revalidateCustomCategories(mutateGlobal);
      } catch (err) {
        showError(err, 'Não foi possível arquivar a categoria.');
      }
    },
    [authedFetch, mutateOverview, mutateGlobal, showError],
  );

  const handleConfirmSelected = useCallback(async (ids: string[]) => {
    setConfirmingAll(true);
    try {
      const { confirmed } = await authedFetch((token) =>
        categoryBudgetApi.reviewBatch(token, { transactionIds: ids }),
      );
      track('action.categorize.apply', {
        source: 'review_queue',
        mode: 'batch_selected',
        count: confirmed,
        rule_created: false,
      });
      toast.success(
        confirmed === 1 ? '1 lançamento confirmado.' : `${confirmed} lançamentos confirmados.`,
      );
      if (userId) markHomeFeedSeenIds(userId, ids);
      refresh();
    } catch (err) {
      showError(err, 'Não foi possível confirmar os lançamentos.');
    } finally {
      setConfirmingAll(false);
    }
  }, [authedFetch, refresh, showError, userId]);

  const handleCategorizeBatch = useCallback(async (ids: string[], categoryRef: string) => {
    setCategorizeSaving(true);
    try {
      const { confirmed, failed } = await authedFetch((token) =>
        categoryBudgetApi.reviewBatch(token, { transactionIds: ids, categoryRef }),
      );
      track('action.categorize.apply', {
        source: 'review_queue',
        mode: 'batch_selected',
        count: confirmed,
        rule_created: false,
      });
      if (failed > 0) toast.warning(`${confirmed} categorizados, ${failed} falharam.`);
      else toast.success(confirmed === 1 ? '1 lançamento categorizado.' : `${confirmed} lançamentos categorizados.`);
      if (userId) markHomeFeedSeenIds(userId, ids);
      setCategorizingBatch(null);
      refresh();
    } catch (err) {
      showError(err, 'Não foi possível categorizar os lançamentos.');
    } finally {
      setCategorizeSaving(false);
    }
  }, [authedFetch, refresh, showError, userId]);

  const handleConfirmAll = useCallback(async () => {
    setConfirmingAll(true);
    try {
      const { confirmed } = await authedFetch((token) =>
        categoryBudgetApi.confirmAll(token),
      );
      track('action.categorize.apply', {
        source: 'review_queue',
        mode: 'confirm_all',
        count: confirmed,
        rule_created: false,
      });
      if (userId) markHomeFeedSeenIds(userId, (queue?.results ?? []).map((queued) => queued.id));
      toast.success(
        confirmed === 1 ? '1 lançamento confirmado.' : `${confirmed} lançamentos confirmados.`,
      );
      refresh();
    } catch (err) {
      showError(err, 'Não foi possível confirmar os lançamentos.');
    } finally {
      setConfirmingAll(false);
    }
  }, [authedFetch, refresh, showError, userId, queue]);

  const handleCreateCategory = useCallback(
    async (data: CreateCustomCategoryRequest, limitAmount: number | null) => {
      setCreatingCategory(true);
      try {
        const { id } = await authedFetch((token) => categoryBudgetApi.createCategory(token, data));
        // Tracked before the optional limit request — the category is already
        // persisted even if the follow-up upsertLimit fails.
        track('action.category.create', {
          via_suggestion: false,
          kind: data.kind,
          with_budget: limitAmount != null,
        });
        if (limitAmount != null) {
          await authedFetch((token) =>
            categoryBudgetApi.upsertLimit(token, id, { limitAmount }),
          );
        }
        toast.success(`Categoria “${data.name}” criada.`);
        setNewCategoryOpen(false);
        void mutateOverview();
        revalidateCustomCategories(mutateGlobal);
      } catch (err) {
        showError(err, 'Não foi possível criar a categoria.');
      } finally {
        setCreatingCategory(false);
      }
    },
    [authedFetch, mutateOverview, mutateGlobal, showError],
  );

  // Accepting the "já existe" nudge in NewCategorySheet: point the user at the existing
  // category instead of creating a near-duplicate. Only close the sheet once
  // resolveCategoryDetailTarget actually succeeds, so a still-loading/failed taxonomy
  // doesn't silently swallow the click and leave the user's draft gone with no navigation
  // to show for it.
  const handleSelectExistingCategory = useCallback(
    (categoryId: string, source: CategoryRefSource) => {
      const target = resolveCategoryDetailTarget(categoryId, source, categoryTree);
      if (!target) {
        toast.error('Não foi possível abrir essa categoria agora. Tente novamente.');
        return;
      }
      setNewCategoryOpen(false);
      openCategoryDetail(target.rootId, target.childId);
    },
    [openCategoryDetail, categoryTree],
  );

  if (overviewError && !overview) {
    return (
      <ErrorState
        title="Não foi possível carregar o orçamento"
        message="Tente novamente em instantes."
        actionLabel="Tentar novamente"
        onAction={() => void mutateOverview()}
      />
    );
  }

  if (overviewLoading && !overview) {
    return (
      <div className="space-y-4">
        <div className="h-9 w-48 animate-pulse rounded-lg bg-muted" />
        <div className="h-96 animate-pulse rounded-3xl bg-muted" />
      </div>
    );
  }

  if (!overview) return null;

  const detailRow = detailCategoryRef
    ? overview.rows.find((r) => r.categoryRef === detailCategoryRef) ?? null
    : null;
  const categoryIndex = buildCategoryIndexFromTaxonomy(pickerCategories, overview.rows);
  const categorizeSubject = categorizing
    ? {
        id: categorizing.id,
        label: categorizing.description,
        currentCategoryId: categorizing.suggestion?.categoryRef ?? null,
        // Review-queue items have no real current category — this is only a suggestion
        // (and for 'user_categorized_before' it's labeled "Sua escolha anterior" elsewhere,
        // which would contradict an "Atual" pin/badge on the same row).
        pinCurrentCategory: false,
      }
    : null;

  return (
    <div className="space-y-4 sm:space-y-5">
      <PageHeader
        title="Categorias"
        description="Todas as suas categorias e subcategorias, com o limite e o uso do mês — em um só lugar."
        titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl"
        actions={
          <Button onClick={() => setNewCategoryOpen(true)}>
            <PlusIcon className="size-4" /> Nova categoria
          </Button>
        }
      />

      {queue && (
        <ReviewQueueCard
          items={queue.results}
          total={queue.total}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          confirmingId={confirmingId}
          confirmingAll={confirmingAll}
          onConfirm={handleConfirm}
          onAlter={setCategorizing}
          onConfirmAll={handleConfirmAll}
          onConfirmSelected={handleConfirmSelected}
          onAlterSelected={setCategorizingBatch}
        />
      )}

      <CategoryRows
        rows={overview.rows}
        childLimits={overview.childLimits}
        categoryIndex={categoryIndex}
        categoryTree={categoryTree}
        ruleRefs={ruleRefs}
        onViewChildTransactions={(rootId, leafId) => openCategoryDetail(rootId, leafId)}
        savingRef={savingLimitRef}
        onSaveLimit={handleSaveLimit}
        onLinkGoal={setGoalPickerRow}
        onReviewChipClick={scrollToReview}
        onEdit={(row) => setEditingCategory({
          id: row.categoryRef,
          name: row.name,
          icon: row.icon,
          color: row.color,
        })}
        onViewTransactions={(row) => openCategoryDetail(row.categoryRef, null)}
        onViewRules={(row) => openCategoryDetail(row.categoryRef, null, 'regras')}
        onViewChildRules={(rootRef) => openCategoryDetail(rootRef, null, 'regras')}
        onToggleArchived={handleToggleArchived}
      />

      <GoalLinkPicker
        row={goalPickerRow}
        saving={savingGoalLink}
        onOpenChange={(open) => {
          if (!open) setGoalPickerRow(null);
        }}
        onSelect={(categoryRef, goalId) => void handleSaveGoalLink(categoryRef, goalId)}
      />

      <NewCategorySheet
        open={newCategoryOpen || editingCategory != null}
        onOpenChange={(open) => {
          if (!open) {
            setNewCategoryOpen(false);
            setEditingCategory(null);
          }
        }}
        saving={creatingCategory}
        onCreate={handleCreateCategory}
        editing={editingCategory}
        onUpdate={handleUpdateCategory}
        onSelectExisting={handleSelectExistingCategory}
      />

      <CategoryPickerDrawer
        open={categorizing != null || (categorizingBatch?.length ?? 0) > 0}
        onOpenChange={(open) => {
          if (!open) {
            setCategorizing(null);
            setCategorizingBatch(null);
          }
        }}
        subject={categorizeSubject}
        categoryIndex={categoryIndex}
        saving={categorizeSaving}
        onSave={(_id, categoryRef, _source, options) => handleCategorize(categorizing!, categoryRef, options)}
        selectedIdsBatch={categorizingBatch
          ? { ids: categorizingBatch, onSave: handleCategorizeBatch }
          : null}
        ruleCreation={categorizing
          ? {
              anchors: buildRuleAnchors(categorizing),
            }
          : undefined}
      />

      <CategoryDetailDrawer
        open={detailCategoryRef != null}
        onOpenChange={(open) => !open && closeCategoryDetail()}
        categoryId={detailCategoryRef}
        initialView={detailView}
        initialSubcategoryId={detailSubcategoryId}
        categoryIndex={categoryIndex}
        dateRange={{ from: monthScope.from, to: monthScope.to }}
        periodOverride={{ preset: 'current_month', from: monthScope.from, to: monthScope.to }}
        overviewScope={overviewScope}
        categoryAmount={detailRow?.spent}
        periodTotal={overview.summary.totalSpent}
        onEditCategory={(row) => setEditingCategory({
          id: row.categoryRef,
          name: row.name,
          icon: row.icon,
          color: row.color,
        })}
        onApplyParentCategoryFilter={(id) => {
          // Deep-links into the transactions category filter — bypasses `patch`,
          // so the filter usage is captured here.
          track('ui.filter_applied', {
            surface: 'transactions',
            filter: 'category',
            action: 'set',
            method: 'drawer',
          });
          router.push(`/transactions?cat=${encodeURIComponent(id)}`);
        }}
        onApplyChildCategoryFilter={(id) => {
          track('ui.filter_applied', {
            surface: 'transactions',
            filter: 'category',
            action: 'set',
            method: 'drawer',
          });
          router.push(`/transactions?cat=${encodeURIComponent(id)}&catMode=exact`);
        }}
        onOpenTransactionDetails={(tx) => {
          if (tx) track('action.transaction.open_detail', { source: 'orcamento' });
          setDetailTx(tx);
        }}
      />

      {/* Stacks over the category hub drawer — back (or any close gesture) returns
          to the hub with its state intact. */}
      <TransactionDetailDrawer
        open={detailTx != null}
        onOpenChange={(o) => setDetailTx(o ? detailTx : null)}
        onBack={closeTransactionDetails}
        tx={detailTx}
        categoryIndex={categoryIndex}
        onRename={rename.handleRename}
        onPreviewRenameBatch={rename.handlePreviewRenameBatch}
        isRenameSaving={rename.isRenameSaving}
      />
    </div>
  );
}
